/**
 * Resolving what the user said into what a filter needs. Shared by every
 * page's agent filter tools (demand grid, OMS surfaces).
 *
 * An unmatched value is an error rather than a silent drop: filtering by two
 * of the three things someone asked for, and saying nothing, is worse than
 * refusing. The message carries the real names so the next attempt can be
 * right.
 */

export interface NamedOption {
  id: string;
  name: string | null;
}

/**
 * Names (what the user says and the page shows) → ids (what the filter
 * takes). Ids are accepted too, so a tool call built from a previous result
 * still works.
 */
export function resolveIds(
  values: string[],
  options: NamedOption[],
  label: string,
): string[] {
  const byName = new Map<string, string>();
  const ids = new Set<string>();
  for (const option of options) {
    ids.add(option.id);
    if (option.name) byName.set(option.name.trim().toLowerCase(), option.id);
  }
  const resolved: string[] = [];
  const unknown: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (ids.has(trimmed)) {
      resolved.push(trimmed);
      continue;
    }
    const match = byName.get(trimmed.toLowerCase());
    if (match) {
      resolved.push(match);
      continue;
    }
    unknown.push(value);
  }
  if (unknown.length > 0) {
    const available = options
      .map((option) => option.name)
      .filter((name): name is string => Boolean(name));
    throw new Error(
      `No ${label} named ${unknown.map((v) => `"${v}"`).join(', ')}. Available: ${available.join(', ') || 'none on this run'}.`,
    );
  }
  return resolved;
}

/** Same, for the filters whose values are the strings themselves. */
export function resolveValues(
  values: string[],
  available: string[],
  label: string,
): string[] {
  const byLower = new Map(
    available.map((value) => [value.trim().toLowerCase(), value]),
  );
  const resolved: string[] = [];
  const unknown: string[] = [];
  for (const value of values) {
    const match = byLower.get(value.trim().toLowerCase());
    if (match) resolved.push(match);
    else unknown.push(value);
  }
  if (unknown.length > 0) {
    throw new Error(
      `No ${label} called ${unknown.map((v) => `"${v}"`).join(', ')}. Available: ${available.join(', ') || 'none on this run'}.`,
    );
  }
  return resolved;
}
